import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useSession } from '../layout/TopBar';
import { formatBytes, useAccountMutations, useProfile, useUsage, type UsageImage } from '../api/account';
import { routeBase, shortDate, useOpenStudy } from '../api/collections';
import { imageThumbnail } from '../api/explore';
import { Avatar } from '../ui/Avatar';
import { Button } from '../ui/Button';
import { confirm } from '../ui/Confirm';
import { Badge, Checkbox, Notice, Segmented, Switch } from '../ui/Controls';
import { Cover } from '../ui/Cards';
import { Field, Input, SearchField } from '../ui/Field';
import { Select } from '../ui/Select';
import { SelectionToolbar } from '../ui/SelectionToolbar';
import { useViewerLight } from '../../features/viewer-controls/viewerTheme';
import { useViewerGlideSetting } from '../../features/viewer-controls/viewerGlide';
import { SHARP_MOTION_BLURB, SHARPNESS_BLURB, useViewerSharpMotionSetting, useViewerSharpnessSetting } from '../../features/viewer-controls/viewerSharpness';
import { XRAY_AUTO_WINDOW_BLURB, useViewerXrayAutoWindowSetting } from '../../features/viewer-controls/xrayAutoWindow';
import { GraphicsCalibrationSection } from '../GraphicsCalibration';

/**
 * Account (`is.account`): a 200px sidebar (avatar, name, tabs) and one of
 * Profile, Usage, Settings. Plan & billing is deferred with billing (decided
 * 2026-09-09), so the sidebar's plan line and that tab are not drawn.
 * Uploading a photo is left for later (same decision): the avatar is the
 * initials.
 */
type Tab = 'profile' | 'usage' | 'settings';
const TABS: Array<{ id: Tab; label: string }> = [{ id: 'profile', label: 'Profile' }, { id: 'usage', label: 'Usage' }, { id: 'settings', label: 'Settings' }];

export function AccountPage() {
  const { tab: raw } = useParams<{ tab: string }>();
  const tab: Tab = raw === 'usage' || raw === 'settings' ? raw : 'profile';
  const { data: me } = useSession();
  const name = me?.displayName || me?.email || '';
  return (
    <div className="tr-page tr-stack" style={{ padding: '36px 28px 44px', display: 'grid', gridTemplateColumns: '200px minmax(0,1fr)', gap: 40, maxWidth: 1040, width: '100%', margin: '0 auto', flex: 1 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Avatar name={name} size={44} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
            <span className="tr-ellipsis" style={{ fontSize: 14, fontWeight: 600 }}>{name}</span>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {TABS.map((t) => (
            <Link
              key={t.id}
              to={`${routeBase()}/account/${t.id}`}
              className="tr-hover-track-ink"
              style={{ padding: '8px 10px', borderRadius: 6, fontSize: 13.5, fontWeight: 500, cursor: 'pointer', textDecoration: 'none', background: tab === t.id ? '#f2f2f0' : 'transparent', color: tab === t.id ? '#0f0f10' : '#6b6b70' }}
            >
              {t.label}
            </Link>
          ))}
        </div>
      </div>
      {tab === 'profile' ? <Profile /> : tab === 'usage' ? <Usage /> : <Settings />}
    </div>
  );
}

function Heading({ title, blurb }: { title: string; blurb: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600, letterSpacing: '-.02em' }}>{title}</h1>
      <p style={{ margin: 0, fontSize: 14, color: '#6b6b70' }}>{blurb}</p>
    </div>
  );
}

// ── Profile ──────────────────────────────────────────────────────────────────

function Profile() {
  const { data: me } = useProfile();
  const m = useAccountMutations();
  const [f, setF] = useState({ firstName: '', lastName: '', title: '', institution: '', linkedin: '' });
  const [pw, setPw] = useState({ current: '', next: '' });
  useEffect(() => {
    if (!me) return;
    setF({ firstName: me.givenName ?? '', lastName: me.familyName ?? '', title: me.title ?? '', institution: me.institution ?? '', linkedin: me.linkedin ?? '' });
  }, [me]);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) => setF({ ...f, [k]: e.target.value });
  const name = me?.displayName || me?.email || '';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
      <Heading title="Profile" blurb="How you appear to other people in your workspaces." />
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <Avatar name={name} size={64} />
      </div>
      <div className="tr-stack-sm" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 20px' }}>
        <Field label="First name"><Input h={36} value={f.firstName} onChange={set('firstName')} /></Field>
        <Field label="Last name"><Input h={36} value={f.lastName} onChange={set('lastName')} /></Field>
        <Field label="Title"><Input h={36} value={f.title} onChange={set('title')} /></Field>
        <Field label="Institution"><Input h={36} value={f.institution} onChange={set('institution')} /></Field>
        <Field label="Email"><Input h={36} type="email" value={me?.email ?? ''} readOnly title="Your address is your sign-in; it cannot be changed here." /></Field>
        <Field label="LinkedIn"><Input h={36} type="url" value={f.linkedin} onChange={set('linkedin')} placeholder="linkedin.com/in/…" /></Field>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <Button variant="primary" size={34} onClick={() => m.saveProfile.mutate({ givenName: f.firstName.trim(), familyName: f.lastName.trim(), title: f.title, institution: f.institution, linkedin: f.linkedin })} disabled={m.saveProfile.isPending}>
          Save changes
        </Button>
      </div>
      {me?.hasPassword ? (
        <>
          <div style={{ height: 1, background: '#ebebea' }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <span style={{ fontSize: 15, fontWeight: 600 }}>Password</span>
            <div className="tr-stack-sm" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 20px' }}>
              <Field label="Current password"><Input h={36} type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} autoComplete="current-password" /></Field>
              <Field label="New password"><Input h={36} type="password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} autoComplete="new-password" /></Field>
            </div>
            <div>
              <Button variant="outlined" size={32} disabled={!pw.current || !pw.next || m.changePassword.isPending} onClick={() => m.changePassword.mutate({ current: pw.current, next: pw.next }, { onSuccess: () => setPw({ current: '', next: '' }) })}>
                Update password
              </Button>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

// ── Usage ────────────────────────────────────────────────────────────────────

type Sort = 'size' | 'newest' | 'oldest';
const SORTS: Array<{ id: Sort; label: string }> = [{ id: 'size', label: 'Largest' }, { id: 'newest', label: 'Newest' }, { id: 'oldest', label: 'Oldest' }];
/** The filters' "all" (not '': Select treats an empty value as unchosen, and leaves it off the list). */
const ALL = '*';
/** The collection filter's value for images filed in none. */
const NO_COLLECTION = '-';

/**
 * Usage: the storage of the workspaces the person OWNS, whoever uploaded to
 * them (server catalog/storage.ts, decided 2026-09-29). The total against the
 * limit, then each owned workspace's share of it, then the images in them,
 * largest first, narrowed by workspace and collection, with the delete that
 * frees space. Workspaces the person is only a member of count against their
 * owners, and are not here.
 */
function Usage() {
  const nav = useNavigate();
  const openStudy = useOpenStudy();
  const { data, error, isLoading, refetch } = useUsage();
  const m = useAccountMutations();
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<Sort>('size');
  const [ws, setWs] = useState(ALL);
  const [col, setCol] = useState(ALL);
  const [sel, setSel] = useState<Record<string, true>>({});
  const rows = data?.images ?? [];
  const owned = data?.workspaces ?? [];
  const wsName = useMemo(() => new Map(owned.map((w) => [w.public_id, w.name])), [owned]);
  // A workspace filter that no longer names one of theirs (handed over, deleted) reads as all.
  const wsOn = ws !== ALL && wsName.has(ws) ? ws : ALL;
  const inWs = useMemo(() => (wsOn === ALL ? rows : rows.filter((r) => r.workspace_id === wsOn)), [rows, wsOn]);
  // The collections the chosen workspace's images are filed in, by name.
  const collections = useMemo(() => {
    const seen = new Map<string, string>();
    let loose = false;
    for (const r of inWs) {
      if (r.collection_id) seen.set(r.collection_id, r.collection_name || 'Untitled collection');
      else loose = true;
    }
    const opts = [...seen].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
    return loose ? [...opts, { value: NO_COLLECTION, label: 'Not in a collection' }] : opts;
  }, [inWs]);
  const colOn = col !== ALL && collections.some((c) => c.value === col) ? col : ALL;
  const list = useMemo(() => {
    const ql = q.trim().toLowerCase();
    const filtered = inWs.filter(
      (r) =>
        (colOn === ALL || (colOn === NO_COLLECTION ? !r.collection_id : r.collection_id === colOn)) &&
        (!ql || [r.original_filename, r.description, r.study_id, r.collection_name, r.group_name].some((t) => (t || '').toLowerCase().includes(ql))),
    );
    return [...filtered].sort((a, b) => (sort === 'size' ? b.bytes - a.bytes : sort === 'newest' ? b.created_utc.localeCompare(a.created_utc) : a.created_utc.localeCompare(b.created_utc)));
  }, [inWs, colOn, q, sort]);
  const used = data?.usedBytes ?? 0;
  const pending = data?.pendingBytes ?? 0;
  const limit = data?.limitBytes ?? 0;
  const share = (bytes: number) => (limit ? Math.min(100, (bytes / limit) * 100) : 0);
  const pct = Math.round(share(used));
  const pendingPct = Math.min(100 - pct, Math.round(share(pending)));
  // What an upload is refused against: stored, plus uploads still on their way (catalog/storage.ts).
  const free = Math.max(0, limit - used - pending);
  const allSel = list.length > 0 && list.every((r) => sel[r.image_id]);
  // Only what the filters show: a delete never reaches an image out of view.
  const selected = list.filter((r) => sel[r.image_id]);
  const selBytes = selected.reduce((a, r) => a + r.bytes, 0);
  const imageName = (r: UsageImage) => r.original_filename || r.description || r.image_id.slice(-8);
  const toggle = (id: string) => setSel((s) => { const n = { ...s }; if (n[id]) delete n[id]; else n[id] = true; return n; });
  const del = async () => {
    if (!selected.length) return;
    if (!(await confirm({ title: `Delete ${selected.length} ${selected.length === 1 ? 'image' : 'images'} (${formatBytes(selBytes)})?`, body: 'This cannot be undone.', action: 'Delete', danger: true }))) return;
    m.deleteImages.mutate(selected.map((r) => r.image_id), { onSuccess: () => setSel({}) });
  };
  const several = owned.filter((w) => !w.archived).length > 1;
  // One being deleted lists no images: it is not offered.
  const wsOptions = [{ value: ALL, label: 'All workspaces' }, ...owned.filter((w) => !w.archived).map((w) => ({ value: w.public_id, label: w.name }))];
  const colOptions = [{ value: ALL, label: 'All collections' }, ...collections];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
      <Heading title="Usage" blurb="Storage used by the workspaces you own, whoever uploaded to them. Delete images you no longer need to free up space; an upload that does not fit is refused." />
      {/* A failed read used to render as an empty page reading "0 MB of 0 MB".
          Say what happened, with the server's own words, and offer a retry. */}
      {error ? (
        <Notice tone="danger">
          <span style={{ flex: 1 }}>Usage could not be loaded: {error instanceof Error ? error.message : 'something went wrong'}.</span>
          <Button variant="outlined" size={28} onClick={() => void refetch()}>Try again</Button>
        </Notice>
      ) : null}
      {isLoading ? <div style={{ fontSize: 13, color: '#8a8a8f' }}>Loading usage…</div> : null}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
          <span style={{ fontSize: 13, color: '#6b6b70' }}>
            Storage · {formatBytes(used)} of {formatBytes(limit)}
            {pending > 0 ? ` · ${formatBytes(pending)} uploading` : ''}
          </span>
          <span style={{ fontSize: 12.5, color: free === 0 && limit ? '#d9342b' : '#8a8a8f' }}>{formatBytes(free)} free</span>
        </div>
        <div style={{ display: 'flex', height: 6, borderRadius: 3, background: '#e8e8e6', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${pct}%`, background: '#0f0f10' }} />
          {pendingPct > 0 ? <div title="Uploads under way" style={{ height: '100%', width: `${pendingPct}%`, background: '#8a8a8f' }} /> : null}
        </div>
      </div>

      {data ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Workspaces you own</h2>
            {owned.length ? <span style={{ fontSize: 13, color: '#8a8a8f' }}>{owned.length}</span> : null}
          </div>
          {owned.length ? (
            <div style={{ border: '1px solid #ebebea', borderRadius: 10, overflow: 'hidden' }}>
              {owned.map((w, i) => {
                const on = wsOn === w.public_id;
                const usedW = Math.round(share(w.usedBytes) * 10) / 10;
                const pendingW = Math.min(100 - usedW, Math.round(share(w.pendingBytes) * 10) / 10);
                return (
                  <button
                    key={w.public_id}
                    type="button"
                    className={several && !w.archived ? 'tr-hover-soft-fg' : undefined}
                    // Narrows the images below to this workspace; again, to all of them.
                    onClick={() => { setWs(on ? ALL : w.public_id); setCol(ALL); }}
                    disabled={!several || w.archived}
                    aria-pressed={several ? on : undefined}
                    style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%', padding: '12px 16px', border: 0, borderTop: i ? '1px solid #f0f0ee' : 0, background: on ? '#fafaf9' : '#fff', textAlign: 'left', fontFamily: 'inherit', color: '#0f0f10', cursor: several && !w.archived ? 'pointer' : 'default' }}
                  >
                    <span style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, width: '100%' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                        <span className="tr-ellipsis" style={{ fontSize: 13.5, fontWeight: on ? 600 : 500, color: w.archived ? '#8a8a8f' : '#0f0f10' }}>{w.name}</span>
                        {w.archived ? <Badge>BEING DELETED</Badge> : null}
                      </span>
                      <span style={{ flex: 'none', fontSize: 12.5, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{formatBytes(w.usedBytes)}</span>
                        {w.pendingBytes > 0 ? <span style={{ color: '#8a8a8f' }}> · {formatBytes(w.pendingBytes)} uploading</span> : null}
                      </span>
                    </span>
                    {/* Its share of the limit, drawn like the storage bar above. */}
                    <span style={{ display: 'flex', width: '100%', height: 4, borderRadius: 2, background: '#efefed', overflow: 'hidden' }}>
                      <span style={{ height: '100%', width: `${usedW}%`, background: '#0f0f10' }} />
                      {pendingW > 0 ? <span style={{ height: '100%', width: `${pendingW}%`, background: '#8a8a8f' }} /> : null}
                    </span>
                  </button>
                );
              })}
            </div>
          ) : (
            <div style={{ padding: '18px 16px', border: '1px solid #ebebea', borderRadius: 10, fontSize: 13, color: '#6b6b70', lineHeight: 1.5 }}>
              You don't own any workspaces. What you upload to someone else's workspace counts against its owner's storage.
            </div>
          )}
        </div>
      ) : null}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, whiteSpace: 'nowrap' }}>
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Images</h2>
            <span style={{ fontSize: 13, color: '#8a8a8f' }}>{list.length} · {formatBytes(list.reduce((a, r) => a + r.bytes, 0))}</span>
          </div>
          <Segmented value={sort} options={SORTS} onChange={setSort} segH={24} bordered />
        </div>
        <div className="tr-wrap-sm" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {several ? (
            <Select value={wsOn} options={wsOptions} onChange={(v) => { setWs(v); setCol(ALL); }} size="sm" width={190} style={{ flex: 'none' }} />
          ) : null}
          <Select value={colOn} options={colOptions} onChange={setCol} size="sm" width={210} disabled={!collections.length} style={{ flex: 'none' }} />
          <div className="tr-basis-full-sm" style={{ flex: 1, minWidth: 0 }}>
            <SearchField value={q} onChange={setQ} placeholder="Filter by name, study, collection" width="100%" h={30} onClear={() => setQ('')} />
          </div>
        </div>
        <div className="tr-table-680" style={{ border: '1px solid #ebebea', borderRadius: 10, overflowX: 'auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '28px 44px minmax(0,1.3fr) minmax(0,1.6fr) 90px 110px', alignItems: 'center', height: 40, padding: '0 16px', background: '#fafaf9', borderBottom: '1px solid #ebebea', fontSize: 12, fontWeight: 500, color: '#6b6b70', gap: 14 }}>
            <div><Checkbox checked={allSel} title="Select all" onToggle={() => setSel(allSel ? {} : Object.fromEntries(list.map((r) => [r.image_id, true])))} /></div>
            <div />
            <div>Image</div>
            <div>Location</div>
            <div style={{ textAlign: 'right' }}>Size</div>
            <div style={{ textAlign: 'right' }}>Added</div>
          </div>
          {list.map((r) => {
            const on = !!sel[r.image_id];
            return (
              <div key={r.image_id} className="tr-hover-soft-fg" style={{ display: 'grid', gridTemplateColumns: '28px 44px minmax(0,1.3fr) minmax(0,1.6fr) 90px 110px', alignItems: 'center', padding: '8px 16px', borderBottom: '1px solid #f0f0ee', gap: 14, background: on ? '#fafaf9' : '#fff' }}>
                <Checkbox checked={on} onToggle={() => toggle(r.image_id)} />
                <Cover src={imageThumbnail(r.image_id)} fallback="#e8e8e6" radius={5} style={{ width: 44, height: 32, border: '1px solid #ebebea' }} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
                  <span className="tr-ellipsis" style={{ fontSize: 13, fontWeight: 500, fontFamily: "'JetBrains Mono', monospace" }}>{imageName(r)}</span>
                  {/* Which workspace, while the list shows more than one. */}
                  {several && wsOn === ALL && r.workspace_id ? <span className="tr-ellipsis" style={{ fontSize: 11.5, color: '#8a8a8f' }}>{wsName.get(r.workspace_id)}</span> : null}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12.5, color: '#6b6b70', minWidth: 0 }}>
                  {r.collection_id ? (
                    <a href="#" className="tr-hover-underline tr-ellipsis" onClick={(e) => { e.preventDefault(); nav(`${routeBase()}/collections/${r.collection_id}`); }} style={{ color: '#0f0f10', textDecoration: 'none' }}>{r.collection_name}</a>
                  ) : (
                    <span className="tr-ellipsis">Not in a collection</span>
                  )}
                  {r.group_name ? (
                    <>
                      <span style={{ color: '#c4c4c2' }}>›</span>
                      <span className="tr-ellipsis">{r.group_name}</span>
                    </>
                  ) : null}
                  <span style={{ color: '#c4c4c2' }}>›</span>
                  <a href="#" className="tr-hover-underline" onClick={(e) => { e.preventDefault(); openStudy(r.study, r.series_instance_uid); }} style={{ color: '#0f0f10', textDecoration: 'none', fontFamily: "'JetBrains Mono', monospace", whiteSpace: 'nowrap' }}>
                    {r.study_id || r.study_description || r.study.slice(-8)}
                  </a>
                </div>
                <div style={{ fontSize: 12.5, fontFamily: "'JetBrains Mono', monospace", textAlign: 'right' }}>{formatBytes(r.bytes)}</div>
                <div style={{ fontSize: 12.5, color: '#6b6b70', textAlign: 'right', whiteSpace: 'nowrap' }}>{shortDate(r.created_utc)}</div>
              </div>
            );
          })}
          {data && list.length === 0 ? <div style={{ padding: '22px 16px', fontSize: 13, color: '#8a8a8f', textAlign: 'center' }}>{rows.length ? 'No images match.' : 'No images yet.'}</div> : null}
        </div>
      </div>
      {selected.length ? (
        <SelectionToolbar
          label={`${selected.length} ${selected.length === 1 ? 'image' : 'images'} · ${formatBytes(selBytes)}`}
          onSelectAll={() => setSel(Object.fromEntries(list.map((r) => [r.image_id, true])))}
          allSelected={allSel}
          onDelete={del}
          onClear={() => setSel({})}
          simple
        />
      ) : null}
    </div>
  );
}

// ── Settings ─────────────────────────────────────────────────────────────────

const VIEWER_THEMES = [
  { id: 'dark', label: 'Dark' },
  { id: 'light', label: 'Light' },
] as const;

const VIEWER_PRIORITY = [
  { id: 'speed', label: 'Speed' },
  { id: 'sharpness', label: 'Sharpness' },
] as const;

const VIEWER_MOVEMENT = [
  { id: 'responsive', label: 'Responsive' },
  { id: 'smooth', label: 'Smooth' },
] as const;

function Settings() {
  const { data: me } = useSession();
  const m = useAccountMutations();
  const on = !!me?.prefs?.openViewerNewTab;
  const [light, setLight] = useViewerLight();
  const [glide, setGlide] = useViewerGlideSetting();
  const [sharp, setSharp] = useViewerSharpnessSetting();
  const [sharpMotion, setSharpMotion] = useViewerSharpMotionSetting();
  const [xrayAuto, setXrayAuto] = useViewerXrayAutoWindowSetting();
  const [pw, setPw] = useState('');
  const [asking, setAsking] = useState(false);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
      <Heading title="Settings" blurb="Preferences that apply to your account." />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 0, border: '1px solid #ebebea', borderRadius: 12, overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 20, padding: '14px 18px', borderBottom: '1px solid #f0f0ee' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontSize: 13.5, fontWeight: 500 }}>Open the image viewer in a new tab</span>
            <span style={{ fontSize: 12.5, color: '#6b6b70', lineHeight: 1.4 }}>Keeps your place in the collection.</span>
          </div>
          <Switch on={on} onToggle={() => m.savePrefs.mutate({ openViewerNewTab: !on })} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '14px 18px', borderBottom: '1px solid #f0f0ee' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 20 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span style={{ fontSize: 13.5, fontWeight: 500 }}>Loading priority</span>
              <span style={{ fontSize: 12.5, color: '#6b6b70', lineHeight: 1.4 }}>{SHARPNESS_BLURB}</span>
            </div>
            <Segmented value={sharp ? 'sharpness' : 'speed'} options={VIEWER_PRIORITY} onChange={(v) => setSharp(v === 'sharpness')} />
          </div>
          {/* Sharpness's own option, out of sight under Speed, where it does nothing. */}
          {sharp && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 20, marginLeft: 4, paddingLeft: 14, borderLeft: '2px solid #ebebea' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontSize: 13, fontWeight: 500 }}>Sharpness in Motion</span>
                <span style={{ fontSize: 12.5, color: '#6b6b70', lineHeight: 1.4 }}>{SHARP_MOTION_BLURB}</span>
              </div>
              <Switch on={sharpMotion} onToggle={() => setSharpMotion(!sharpMotion)} />
            </div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 20, padding: '14px 18px', borderBottom: '1px solid #f0f0ee' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontSize: 13.5, fontWeight: 500 }}>Movement</span>
            <span style={{ fontSize: 12.5, color: '#6b6b70', lineHeight: 1.4 }}>
              The image follows your hand precisely, staying where you release it or gliding naturally before gently coming to a stop with a quick movement.
            </span>
          </div>
          <Segmented value={glide ? 'smooth' : 'responsive'} options={VIEWER_MOVEMENT} onChange={(v) => setGlide(v === 'smooth')} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 20, padding: '14px 18px', borderBottom: '1px solid #f0f0ee' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontSize: 13.5, fontWeight: 500 }}>Image viewer theme</span>
            <span style={{ fontSize: 12.5, color: '#6b6b70', lineHeight: 1.4 }}>Dark by default. You can also switch it at the bottom of the viewer.</span>
          </div>
          <Segmented value={light ? 'light' : 'dark'} options={VIEWER_THEMES} onChange={(v) => setLight(v === 'light')} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 20, padding: '14px 18px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontSize: 13.5, fontWeight: 500 }}>X-ray auto window and level</span>
            <span style={{ fontSize: 12.5, color: '#6b6b70', lineHeight: 1.4 }}>{XRAY_AUTO_WINDOW_BLURB}</span>
          </div>
          <Switch on={xrayAuto} onToggle={() => setXrayAuto(!xrayAuto)} />
        </div>
      </div>
      <div style={{ height: 1, background: '#ebebea' }} />
      <GraphicsCalibrationSection />
      <div style={{ height: 1, background: '#ebebea' }} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <span style={{ fontSize: 15, fontWeight: 600, color: '#b3261e' }}>Delete account</span>
        <span style={{ fontSize: 13, color: '#6b6b70', lineHeight: 1.5, maxWidth: 520 }}>Permanently removes your profile. Workspaces you own that other people are in must first be handed to another administrator; the ones only you are in are deleted with everything in them.</span>
        {asking ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 320 }}>
            {me?.hasPassword !== false ? <Field label="Your password"><Input h={36} type="password" value={pw} onChange={(e) => setPw(e.target.value)} autoFocus autoComplete="current-password" /></Field> : null}
            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                variant="outlined"
                size={32}
                style={{ color: '#b3261e' }}
                className="tr-hover-danger2"
                disabled={m.deleteAccount.isPending || (me?.hasPassword !== false && !pw)}
                onClick={async () => {
                  if (!(await confirm({ title: 'Delete your account?', body: 'Your profile, comments and memberships are removed, and workspaces only you are in are deleted with every image in them. This cannot be undone.', action: 'Delete my account', danger: true }))) return;
                  m.deleteAccount.mutate(me?.hasPassword !== false ? pw : undefined, { onSuccess: () => window.location.assign(`${import.meta.env.BASE_URL.replace(/\/+$/, '')}/explore`) });
                }}
              >
                Delete my account
              </Button>
              <Button variant="ghost" size={32} onClick={() => setAsking(false)}>Cancel</Button>
            </div>
          </div>
        ) : (
          <div>
            <Button variant="outlined" size={32} style={{ color: '#b3261e' }} className="tr-hover-danger2" onClick={() => setAsking(true)}>
              Delete my account…
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
